/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo } from 'react';
import { Action, State } from '@table-library/react-table-library/common';
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { CellTree, TreeExpandClickTypes, useTree } from '@table-library/react-table-library/tree';
import { CellSelect, SelectTypes as TreeSelectTypes, useRowSelect } from '@table-library/react-table-library/select';
import { CustomReportSubsidiarySurvey, SURVEY_TYPE_LABELS } from '../../../types/custom-report';
import { DataPeriods, getPeriodName } from '@utils/dataPeriods';
import { DATE, formatDateUTC } from '../../../utils/date';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { useTheme } from '@table-library/react-table-library/theme';
import { Loader } from '@g17eco/atoms/loader';
import { naturalSort } from '../../../utils';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { Filters, SurveyFilters, getSurveyFilters } from '../util/filters';
import { useSurveyFilters } from '../hooks/useSurveyFilters';
import { SURVEY } from '@constants/terminology';
import { StringIdTableNode, TableData } from '../util/treeNavigation';
import { useGetSurveysByInitiativeIdsForCustomReportQuery } from '@api/custom-reports';
import { useSiteAlert } from '@hooks/useSiteAlert';

export interface TableNodeWithSurveyIds extends StringIdTableNode {
  name: string;
  period: DataPeriods;
  effectiveDate: string;
  surveyCount: number;
  surveyType: SurveyType;
}

export interface SurveySelectionTableProps {
  initiativeId: string;
  initiativeIds: string[];
  selectedNodeIds: string[];
  handleSelectedNodes: (selectedNodes?: TableNodeWithSurveyIds[]) => void;
  isMulti?: boolean;
  enabledFilters?: (keyof Filters)[];
  defaultFilters?: Partial<Filters>;
  hideCount?: boolean;
}

const buildTree = (list: CustomReportSubsidiarySurvey[], filters: Filters): TableData => {
  const yearMap = new Map<string, Map<SurveyType, Map<string, CustomReportSubsidiarySurvey[]>>>();

  list.forEach((survey) => {
    const { period, type = SurveyType.Default } = survey;

    const monthYearPeriod = `${survey.effectiveDate} ${period}`;

    const year = formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY);
    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }
    const surveyTypeMap = yearMap.get(year);
    if (!surveyTypeMap) return;

    if (!surveyTypeMap.has(type)) {
      surveyTypeMap.set(type, new Map());
    }

    const monthYearMap = surveyTypeMap.get(type);
    if (!monthYearMap) return;

    if (!monthYearMap.has(monthYearPeriod)) {
      monthYearMap.set(monthYearPeriod, []);
    }

    const monthYearList = monthYearMap.get(monthYearPeriod);
    if (!monthYearList) return;

    monthYearList.push(survey);
  });

  const data: TableData = {
    nodes: [],
  };

  yearMap.forEach((surveyTypeMap, year) => {
    const yearNodes: StringIdTableNode[] = [];

    if (filters.year.length && !filters.year.includes(year)) {
      return;
    }

    surveyTypeMap.forEach((monthYearMap, surveyType) => {
      if (filters.surveyType.length && !filters.surveyType.includes(surveyType)) {
        return;
      }
      const periodNodes: TableNodeWithSurveyIds[] = [];

      monthYearMap.forEach((monthYearList, monthYearPeriod) => {
        const currentDate = monthYearList[0].effectiveDate;
        if (filters.month.length && !filters.month.includes(formatDateUTC(currentDate, 'M'))) {
          return;
        }

        const [effectiveDate, period] = monthYearPeriod.split(' ');
        if (filters.period.length && !filters.period.includes(period as DataPeriods)) {
          return;
        }
        const monthYear = formatDateUTC(effectiveDate, DATE.MONTH_YEAR);
        periodNodes.push({
          id: `${year}-${surveyType}-${monthYear}-${period}`,
          name: `${monthYear} ${getPeriodName(period as DataPeriods, false)}`,
          effectiveDate: currentDate,
          period: period as DataPeriods,
          periodName: `${getPeriodName(period as DataPeriods, false)}`,
          surveyCount: monthYearList.length,
          surveyType,
        });
      });

      if (periodNodes.length === 0) {
        return;
      }

      yearNodes.push({
        id: `${year}-${surveyType}`,
        name: SURVEY_TYPE_LABELS[surveyType],
        nodes: periodNodes,
      });
    });

    if (yearNodes.length === 0) {
      return;
    }

    data.nodes.push({
      id: year,
      name: year,
      nodes: yearNodes,
    });
  });

  // Sort DESC by year
  data.nodes.sort((a, b) => naturalSort(b.id, a.id));

  return data;
};

const findNodesByIds = (tableData: TableData, ids?: string[]): TableNodeWithSurveyIds[] => {
  if (!ids || ids.length === 0) {
    return [];
  }
  const nodesMatch: TableNodeWithSurveyIds[] = [];
  tableData.nodes.forEach((year) =>
    year.nodes?.forEach((period) => {
      const matches = period.nodes?.filter((monthYear) => ids.includes(monthYear.id)) as TableNodeWithSurveyIds[];
      if (matches.length > 0) {
        nodesMatch.push(...matches);
      }
    })
  );
  return nodesMatch;
};

export const SurveySelectionTable = (props: SurveySelectionTableProps) => {
  const {
    initiativeId,
    initiativeIds,
    selectedNodeIds,
    handleSelectedNodes,
    isMulti = false,
    enabledFilters = Object.values(SurveyFilters),
    defaultFilters,
    hideCount,
  } = props;

  const { filters, toggleFilter } = useSurveyFilters(defaultFilters);
  const {
    data: surveyListRaw,
    isFetching,
    error,
  } = useGetSurveysByInitiativeIdsForCustomReportQuery(
    {
      initiativeId,
      initiativeIds,
      surveyType: filters.surveyType.length === 1 ? filters.surveyType[0] : undefined,
    },
    { skip: !initiativeId || initiativeIds.length === 0 }
  );
  const surveyList = useMemo(() => surveyListRaw ?? [], [surveyListRaw]);
  const tableData = useMemo(() => buildTree(surveyList, filters), [surveyList, filters]);

  const { addSiteError } = useSiteAlert();

  useEffect(() => {
    if (!initiativeIds.length) {
      return handleSelectedNodes();
    }

  }, [handleSelectedNodes, initiativeIds]);

  useEffect(() => {
    if (error) {
      addSiteError(error.message ?? 'There was an error trying to get survey list');
    }
  }, [error, addSiteError]);

  const expandedIds = useMemo(() => {
    if (selectedNodeIds.length) {
      return selectedNodeIds.reduce((ids, curNodeId) => {
        const parts = curNodeId.split('-');
        // Must be 4 parts, `${year}-${surveyType}-${monthYear}-${period}`
        return parts.length === 4 ? [...ids, parts[0], `${parts[0]}-${parts[1]}`] : ids;
      }, [] as string[]);
    }

    const firstNode = tableData.nodes[0]?.id;
    return firstNode ? [firstNode] : [];
  }, [selectedNodeIds, tableData.nodes]);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 50% repeat(1, minmax(0, 2fr)) repeat(1, minmax(0, 1fr));
    `,
  });

  const tree = useTree(tableData,
    { state: { ids: expandedIds } },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa mx-2' />,
        iconRight: <i className='fa fa-caret-right text-ThemeAccentExtradark mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeAccentExtradark mx-2' />,
      },
      clickType: TreeExpandClickTypes.RowClick,
    }
  );

  const select = useRowSelect(tableData,
    {
      state: { ids: selectedNodeIds },
      onChange: isMulti ? onMultipleSelectChange : onSelectChange,
    },
    {
      rowSelect: isMulti ? TreeSelectTypes.MultiSelect : TreeSelectTypes.SingleSelect,
      buttonSelect: isMulti ? TreeSelectTypes.MultiSelect : TreeSelectTypes.SingleSelect,
    }
  );

  const selectedNodes = findNodesByIds(tableData, isMulti ? select.state.ids : [select.state.id]);

  const updateSelectedNode = (ids: string[]) => {
    const selectedNodes = findNodesByIds(tableData, ids);
    handleSelectedNodes(selectedNodes);
  };

  function onMultipleSelectChange(action: Action, state: State) {
    return updateSelectedNode(state.ids ?? []);
  }

  function onSelectChange(action: Action, state: State) {
    return updateSelectedNode(state.id ? [state.id] : []);
  }

  const yearOptions = useMemo(() => {
    const years = Array.from(
      new Set(surveyList.map((survey) => formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY))).values()
    ).sort();
    return years.map((year) => ({ label: year, value: year }));
  }, [surveyList]);

  const surveyFilters = getSurveyFilters({ yearOptions });

  return (
    <>
      <div className='d-flex align-items-center gap-2'>
        {enabledFilters.map((filter) => {
          const { label, options } = surveyFilters[filter];
          return (
            <SelectFactory
              selectType={SelectTypes.MultipleSelect}
              className='flex-fill'
              key={filter}
              placeholder={label}
              options={options}
              values={filters[filter]}
              onChange={(op) => toggleFilter(filter, op)}
              isFlexibleSize
            />
          )
        })}
        {hideCount ? null : <div className='ms-3'>{`${selectedNodes.length} ${SURVEY.PLURAL} selected`}</div>}
      </div>
      <div className='mt-2 collapsible-table'
        style={{
          height: '400px',
        }}>
        {isFetching ? <Loader /> :
          tableData.nodes.length > 0 ?
          <Table data={tableData} tree={tree} select={select} theme={theme} layout={{ fixedHeader: true, custom: true }}>
            {(tableList: StringIdTableNode[]) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>{SURVEY.CAPITALIZED_SINGULAR}</HeaderCell>
                    <HeaderCell>{`Subsidiaries with ${SURVEY.SINGULAR}`}</HeaderCell>
                    <HeaderCell />
                  </HeaderRow>
                </Header>

                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <CellTree item={item}>{item.name}</CellTree>
                      <Cell>{item.surveyCount}</Cell>
                      {Array.isArray(item.nodes) ? <Cell /> : <CellSelect item={item} />}
                    </Row>
                  ))}
                </Body>
              </>
            )}
          </Table>
          :
          <BasicAlert className='mt-3' type='warning'>{`No ${SURVEY.PLURAL} available for the selected subsidiaries`}</BasicAlert>
        }
      </div>
    </>
  );
};
